/**
 * Pure branching logic for the guest flow's magic-link redemption response.
 * Kept free of DOM/fetch calls so it can be unit-tested with plain `node --test`.
 *
 * The backend used to have its own pending/admitted state machine (the guest
 * polled until a host manually admitted them). That's gone: RealtimeKit's own
 * native waiting room now handles "wait for host approval" once the guest
 * connects with a valid authToken, so a successful redemption just means
 * "here's your token" — no more `status` field to branch on.
 */

import { parseCallType, type CallType } from "./call-type.ts";

export interface MeetingSummary {
  id: string;
  title: string;
}

export type GuestFlowState =
  | { kind: "ready"; authToken: string; meeting: MeetingSummary; callType: CallType }
  | { kind: "error" };

/**
 * Maps a raw `GET /api/meet/:token` response to a `GuestFlowState`.
 *
 * @param ok - whether the HTTP response was a 2xx (per fetch's `Response.ok`).
 * @param body - the parsed JSON body, or `undefined` if it couldn't be parsed.
 */
export function resolveGuestStatus(ok: boolean, body: unknown): GuestFlowState {
  if (!ok || typeof body !== "object" || body === null) {
    return { kind: "error" };
  }

  const record = body as Record<string, unknown>;
  if (typeof record.authToken !== "string") {
    return { kind: "error" };
  }

  const meetingRaw = record.meeting as Record<string, unknown> | undefined;
  const meeting: MeetingSummary = {
    id: typeof meetingRaw?.id === "string" ? meetingRaw.id : "",
    title: typeof meetingRaw?.title === "string" ? meetingRaw.title : "",
  };

  return {
    kind: "ready",
    authToken: record.authToken,
    meeting,
    callType: parseCallType(record.callType),
  };
}
